import { FC } from 'react';
import {
    Avatar,
    Card,
    Space,
    Tag,
    Typography,
    Row,
} from 'antd';
import {
    ThunderboltOutlined,
    CloseCircleOutlined,
    WalletOutlined,
    DollarOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { IOrderSearch } from '../../../../interfaces/requests.interface';
import { DriverSearchTimeline } from './DriverSearchTimeline';
import { getBatteryClass, getReasonIcon } from './utils/orderSearch.utils';

const { Text } = Typography;

interface DriverItemProps {
    driver: IOrderSearch;
}

const DriverItem: FC<DriverItemProps> = ({ driver }) => {
    const { t } = useTranslation('small-fleet-orders');

    const isListed = driver.metadata.wasListed;
    const showBattery = !isListed && driver.metadata.rejectedReason?.toUpperCase() === 'BATTERY';

    const renderReasonIcon = (iconType: string) => {
        switch (iconType) {
        case 'thunderbolt':
            return <ThunderboltOutlined />;
        case 'wallet':
            return <WalletOutlined />;
        case 'dollar':
            return <DollarOutlined />;
        default:
            return <CloseCircleOutlined />;
        }
    };

    if (!isListed) {
        return (
            <Card
                key={driver._id}
                style={{ marginBottom: 12 }}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Space size={8} align="center">
                        <Avatar
                            src={(
                                <Text strong style={{ fontSize: 12, color: 'var(--color-white)' }}>
                                    {driver.driverName?.charAt(0)}
                                </Text>
                            )}
                            style={{
                                backgroundColor: 'var(--color-purple)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            size={24}
                        />
                        <Space direction="vertical" size={4}>
                            <Space size={8}>
                                <Text strong>{driver.driverName}</Text>
                                <Tag color="error">
                                    {t(driver.metadata.rejectedReason?.toUpperCase() || 'NOT_LISTED')}
                                </Tag>
                            </Space>
                            <Text type="secondary" style={{ fontSize: 13 }}>{driver.driverEmail}</Text>
                        </Space>
                    </Space>
                </Space>
            </Card>
        );
    }

    return (
        <Card
            key={driver._id}
            style={{ marginBottom: 16 }}
            styles={{ body: { padding: '12px 16px' } }}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Row justify="space-between" align="middle">
                    <Space size={8}>
                        <Avatar
                            src={(
                                <Text strong style={{ fontSize: 12, color: 'var(--color-white)' }}>
                                    {driver.driverName?.charAt(0)}
                                </Text>
                            )}
                            style={{
                                backgroundColor: 'var(--color-purple)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            size={28}
                        />
                        <Space direction="vertical" size={0}>
                            <Space size={4} align="center">
                                <Text strong>{driver.driverName}</Text>
                                <Tag color="green" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
                                    {t('LISTED')}
                                </Tag>
                            </Space>
                            <Text type="secondary" style={{ fontSize: 12 }}>{driver.driverEmail}</Text>
                        </Space>
                    </Space>
                    {(isListed || showBattery) && (
                        <Space
                            direction="horizontal"
                            size={4}
                            style={{
                                color: getBatteryClass(driver.metadata.batteryPercentage),
                                fontSize: 12,
                            }}
                        >
                            <ThunderboltOutlined style={{ verticalAlign: 'middle' }} />
                            {` ${driver.metadata.batteryPercentage}%`}
                        </Space>
                    )}
                </Row>
                <DriverSearchTimeline metadata={driver.metadata} />
            </Space>
        </Card>
    );
};

export { DriverItem };
